import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { LEGAL_ENTITY_NAME, LEGAL_ENTITY_ADDRESS, PRIVACY_CONTACT_EMAIL } from '../../config';

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="2026-09-30">
      <p>
        This Privacy Policy explains what information {LEGAL_ENTITY_NAME} ("Sheba
        Smart Home," "we," "us") collects through the Sheba website, mobile
        application, and customer portal (together, the "Services"), why we
        collect it, and the choices and rights you have.
      </p>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">1. Who we are</h2>
        <p>
          {LEGAL_ENTITY_NAME}
          <br />
          {LEGAL_ENTITY_ADDRESS}
          <br />
          Contact: <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`} className="text-[#00d4ff]">{PRIVACY_CONTACT_EMAIL}</a>
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">2. Information we collect</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Account information</strong> — name, email address, and password (stored as a one-way hash, never in plain text).</li>
          <li><strong>Smart-home data</strong> — the homes, rooms, devices, scenes, and automations you create, and the state/status those devices report (e.g. on/off, temperature, motion events).</li>
          <li><strong>Camera and video data</strong> — if you connect a camera, video streams and recordings are relayed and/or stored according to your account's configuration; we do not use your camera footage for any purpose other than providing the Services to you.</li>
          <li><strong>Technical information</strong> — IP address, device/browser type, and diagnostic logs, collected automatically to keep the Services secure and reliable.</li>
          <li><strong>Communications</strong> — anything you send us through a contact form, support request, or account-related email.</li>
          <li><strong>Purchase information</strong> (where the Shop feature is available) — order details and shipping information. We do not store full payment card numbers; payment is processed by a third-party payment provider.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">3. How we use your information</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>To create and maintain your account and provide the Services.</li>
          <li>To operate your smart-home devices, automations, and notifications.</li>
          <li>To communicate with you about your account, security, or support requests.</li>
          <li>To maintain the security, integrity, and reliability of the Services.</li>
          <li>To comply with legal obligations.</li>
        </ul>
        <p>We do not sell your personal information.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">4. Who we share information with</h2>
        <p>
          We use a small number of service providers ("processors") to operate the
          Services. Each processes only the data necessary for the function it
          performs:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Database hosting</strong> — MongoDB, to store your account and smart-home data.</li>
          <li><strong>Email delivery</strong> — an email provider (configured per deployment) used to send account, security, and notification emails.</li>
          <li><strong>Push notifications</strong> — Expo's push notification service, used to deliver mobile alerts; it receives only a device push token, not your account data.</li>
          <li><strong>Third-party ecosystem integrations</strong> — if you choose to connect a third-party smart-home ecosystem (for example Tuya or Home Assistant), we exchange only the data necessary to control the devices you've linked, using credentials you provide and control.</li>
          <li><strong>Payment processing</strong> (where applicable) — a third-party payment provider processes transactions; we do not receive or store your full card details.</li>
        </ul>
        <p>We do not share your data with anyone else except where required by law.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">5. Data retention</h2>
        <p>
          We retain your account and smart-home data for as long as your account is
          active. If you delete your account (see our{' '}
          <a href="/account-deletion" className="text-[#00d4ff]">Account Deletion</a>{' '}
          page), we delete your personal data within a reasonable period, except
          where we are required to retain certain records (for example, security
          logs or transaction records) for a limited time to comply with legal
          obligations or to prevent fraud and abuse.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">6. Your rights</h2>
        <p>Depending on where you live, you may have the right to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Access the personal data we hold about you.</li>
          <li>Request correction of inaccurate data.</li>
          <li>Request deletion of your data (see <a href="/account-deletion" className="text-[#00d4ff]">Account Deletion</a>).</li>
          <li>Request a copy of your data in a portable format.</li>
          <li>Object to or restrict certain processing.</li>
        </ul>
        <p>
          To exercise any of these rights, contact us at{' '}
          <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`} className="text-[#00d4ff]">{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
      </section>

      <section id="cookies">
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">7. Cookies</h2>
        <p>
          The website uses a minimal set of cookies: strictly necessary cookies
          that keep you signed in (an encrypted session cookie), and, where
          enabled, anonymized analytics cookies to help us understand how the
          site is used. We do not use cookies for third-party advertising. You
          can control cookies through your browser's settings; disabling
          strictly-necessary cookies will prevent you from staying signed in.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">8. Security</h2>
        <p>
          We use industry-standard measures to protect your data, including
          encrypted password storage, encrypted connections (HTTPS/TLS) for all
          Service traffic, and access controls limiting who can view your data
          internally.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">9. Children's privacy</h2>
        <p>
          The Services are not directed to children under 16, and we do not
          knowingly collect personal information from them.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">10. Changes to this policy</h2>
        <p>
          We may update this policy from time to time. Material changes will be
          reflected by updating the "Last updated" date above.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mt-8 mb-2">11. Contact</h2>
        <p>
          Questions about this policy or your data: {' '}
          <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`} className="text-[#00d4ff]">{PRIVACY_CONTACT_EMAIL}</a>.
        </p>
      </section>
    </LegalPageLayout>
  );
}
